import {
  accountSearchColumns,
  customerSearchColumns,
  hoaDonSearchColumns,
  QUERY_KEYS,
  taiKhoanNganHangSearchColumns,
  thueSearchColumns
} from '@/constants';
import { AccountModel, KhachHang, NgoaiTe, TaiKhoan, TaiKhoanNganHang, Tax } from '@/types/schemas';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import type { InputTableCellParams, InputTableColumn } from '@/components/custom/erp';
import { headColumns, tailColumns } from './headDetailCols';
import { FormMode } from '@/types/form';

export const getDetailTableColumns = (
  onCellValueChange: (rowUuid: string, field: string, newValue: unknown) => void,
  status?: string,
  loaiHD?: string,
  setLoaiHD?: (value: string) => void,
  getCustomerBalance?: (customerUuid: string, accountUuid?: string) => string,
  dien_giai?: string | null,
  formMode?: FormMode
): InputTableColumn[] => {
  const middle1Columns = [
    {
      field: 'du_cn',
      headerName: 'Dư công nợ',
      width: 120,
      renderCell: (params: InputTableCellParams) => {
        const customerUuid = params.row.ma_kh_data?.uuid;
        const balance =
          customerUuid && getCustomerBalance
            ? getCustomerBalance(customerUuid, params.row.tk_no_data?.uuid)
            : params.row.du_cn || '0.00';

        return <CellField name='du_cn' type='number' value={balance} />;
      },
      nonWritable: true
    },
    {
      field: 'id_hd',
      headerName: 'Hóa đơn',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <SearchField<Record<string, unknown>>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.HOA_DON_THEO_KHACH_HANG}/?ma_kh=${params.row.ma_kh_data?.uuid}&document_type=GIAY_BAO_NO`}
          searchColumns={hoaDonSearchColumns}
          columnDisplay='id'
          dialogTitle='Danh mục hóa đơn'
          value={params.row.id_hd_data?.so_ct}
          disabled={formMode === 'view'}
          onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'id_hd_data', row)}
        />
      )
    },
    {
      field: 'so_ct0_hd',
      headerName: 'Số hóa đơn',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField name='so_ct0_hd' type='text' value={params.row.id_hd_data?.so_hd} />
      ),
      nonWritable: true
    },
    {
      field: 'ngay_ct_hd',
      headerName: 'Ngày hóa đơn',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField name='ngay_ct_hd' type='date' value={params.row.id_hd_data?.ngay_ct} />
      ),
      nonWritable: true
    },
    {
      field: 'tk_no',
      headerName: 'Tài khoản nợ',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <SearchField<TaiKhoan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_no_data?.code || ''}
          disabled={formMode === 'view'}
          onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_no_data', row)}
        />
      )
    },
    {
      field: 'ma_nt_hd',
      headerName: 'Ngoại tệ',
      width: 100,
      renderCell: (params: InputTableCellParams) => (
        <CellField name='ma_nt_hd' type='text' value={params.row.id_hd_data?.ngoai_te} disabled={formMode === 'view'} />
      ),
      nonWritable: true
    },
    {
      field: 'ty_gia_hd',
      headerName: 'Tỷ giá hđ',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField name='ty_gia_hd' type='number' value={params.row.ty_gia_hd || 0.0} disabled={formMode === 'view'} />
      ),
      nonWritable: true
    },
    {
      field: 'tien_hd_nt',
      headerName: 'Tiền trên hóa đơn',
      width: 150,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='tien_hd_nt'
          type='number'
          value={params.row.id_hd_data?.tien_tren_hd}
          disabled={formMode === 'view'}
        />
      ),
      nonWritable: true
    },
    {
      field: 'da_pb_nt',
      headerName: 'Đã phân bổ',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='da_pb_nt'
          type='number'
          value={params.row.id_hd_data?.da_thanh_toan ?? params.row.da_pb_nt ?? 0}
          disabled={formMode === 'view'}
        />
      ),
      nonWritable: true
    },
    {
      field: 'cl_nt',
      headerName: 'Còn lại',
      width: 140,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='cl_nt'
          type='number'
          value={params.row.cl_nt || params.row.id_hd_data?.tien_tren_hd}
          disabled={formMode === 'view'}
        />
      ),
      nonWritable: true
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 160,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='tien_nt'
          type='number'
          value={params.row.tien_nt || (params.row.id_hd_data?.tien_tren_hd ? params.row.id_hd_data.tien_tren_hd : 0)}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
          disabled={formMode === 'view'}
        />
      )
    }
  ];

  const middle2Columns = [
    {
      field: 'du_cn',
      headerName: 'Dư công nợ',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='du_cn'
          type='number'
          value={params.row.du_cn || 0.0}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'du_cn', newValue)}
          disabled={formMode === 'view'}
        />
      ),
      nonWritable: true
    },
    {
      field: 'tknh2',
      headerName: 'Ngân hàng',
      width: 180,
      renderCell: (params: InputTableCellParams) => (
        <SearchField<TaiKhoanNganHang>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/`}
          searchColumns={taiKhoanNganHangSearchColumns}
          columnDisplay='account_code'
          dialogTitle='Danh mục tài khoản ngân hàng'
          value={params.row.tk_ngan_hang?.account_code || ''}
          disabled={formMode === 'view'}
          onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_ngan_hang', row)}
        />
      ),
      pasteConfig: {
        lookupKey: 'tknh2',
        resultField: 'tk_ngan_hang',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN_NGAN_HANG
      }
    },
    {
      field: 'tk_no',
      headerName: 'Tài khoản nợ',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <SearchField<AccountModel>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_no_data?.code || ''}
          disabled={formMode === 'view'}
          onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_no_data', row)}
        />
      )
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='tien_nt'
          type='number'
          value={params.row.tien_nt || 0.0}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
          disabled={formMode === 'view'}
        />
      )
    }
  ];

  const middle3Columns = [
    {
      field: 'du_cn',
      headerName: 'Dư công nợ',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='du_cn'
          type='number'
          value={params.row.du_cn || 0.0}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'du_cn', newValue)}
          disabled={formMode === 'view'}
        />
      ),
      nonWritable: true
    },
    {
      field: 'tk_no',
      headerName: 'Tài khoản nợ',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <SearchField<AccountModel>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_no_data?.code || ''}
          disabled={formMode === 'view'}
          onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_no_data', row)}
        />
      )
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='tien_nt'
          type='number'
          value={params.row.tien_nt || 0.0}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
          disabled={formMode === 'view'}
        />
      )
    },
    {
      field: 'ma_loai_hd',
      headerName: 'Loại hoá đơn',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='ma_loai_hd'
          type='select'
          options={[
            { value: '0', label: '0. Không có hoá đơn' },
            { value: '1', label: '1. Hoá đơn GTGT' },
            { value: '2', label: '2. Hoá đơn bán hàng thông thường' }
          ]}
          value={params.row.ma_loai_hd || '0'}
          onValueChange={newValue => {
            setLoaiHD?.(newValue);
            onCellValueChange(params.row.uuid, 'ma_loai_hd', newValue);
          }}
          disabled={formMode === 'view'}
        />
      )
    },
    ...(loaiHD !== '0'
      ? [
          {
            field: 'ma_thue',
            headerName: 'Mã thuế',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <SearchField<Tax>
                type='text'
                searchEndpoint={`/${QUERY_KEYS.THUE}/`}
                searchColumns={thueSearchColumns}
                columnDisplay='ma_thue'
                dialogTitle='Danh mục thuế'
                value={params.row.ma_thue_data?.ma_thue || ''}
                disabled={formMode === 'view'}
                onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_thue_data', row)}
              />
            ),
            pasteConfig: {
              lookupKey: 'ma_thue',
              resultField: 'ma_thue_data',
              detailEndpoint: QUERY_KEYS.THUE
            }
          },
          {
            field: 'ten_thue',
            headerName: 'Tên thuế',
            width: 150,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ten_thue'
                type='text'
                value={params.row.ma_thue_data?.ten_thue || ''}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'thue_suat',
            headerName: 'Thuế suất',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='thue_suat'
                type='number'
                value={params.row.ma_thue_data?.thue_suat || 0.0}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'tk_thue',
            headerName: 'Tk thuế',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <SearchField<AccountModel>
                type='text'
                searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
                searchColumns={accountSearchColumns}
                columnDisplay='code'
                dialogTitle='Danh mục tài khoản'
                value={params.row.tk_thue_data?.code || ''}
                disabled={formMode === 'view'}
                onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_thue_data', row)}
              />
            ),
            pasteConfig: {
              lookupKey: 'tk_thue',
              resultField: 'tk_thue_data',
              detailEndpoint: QUERY_KEYS.TAI_KHOAN
            }
          },
          {
            field: 'ten_tk_thue',
            headerName: 'Tên TK thuế',
            width: 150,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ten_tk_thue'
                type='text'
                value={params.row.tk_thue_data?.name || ''}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'so_ct0',
            headerName: 'Số hoá đơn',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='so_ct0'
                type='text'
                value={params.row.so_ct0 || ''}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct0', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'so_ct2',
            headerName: 'Ký hiệu',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='so_ct2'
                type='text'
                value={params.row.so_ct2 || ''}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct2', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'ngay_ct0',
            headerName: 'Ngày hoá đơn',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ngay_ct0'
                type='date'
                value={params.row.ngay_ct0 || ''}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_ct0', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'ma_mau_ct',
            headerName: 'Mẫu HĐ',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <SearchField<Record<string, unknown>>
                type='text'
                searchEndpoint={`/${QUERY_KEYS.MAU_HOA_DON}/`}
                searchColumns={accountSearchColumns}
                columnDisplay='ma_mau_ct'
                dialogTitle='Danh mục mẫu chứng từ'
                value={params.row.ma_mau_ct_data?.ma_mau_ct || ''}
                disabled={formMode === 'view'}
                onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_mau_ct_data', row)}
              />
            ),
            pasteConfig: {
              lookupKey: 'ma_mau_ct',
              resultField: 'ma_mau_ct_data',
              detailEndpoint: QUERY_KEYS.MAU_HOA_DON
            }
          },
          {
            field: 'ten_mau_ct',
            headerName: 'Tên mẫu HĐ',
            width: 150,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ten_mau_ct'
                type='text'
                value={params.row.ma_mau_ct_data?.ten_mau_ct || ''}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'ma_mau_bc',
            headerName: 'Mẫu báo cáo',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ma_mau_bc'
                type='select'
                options={[
                  { value: '3', label: '3. Hóa đơn giá trị gia tăng' },
                  { value: '4', label: '4. Hàng hóa, dịch vụ mua vào không có hóa đơn' },
                  { value: '5', label: '5. Hóa đơn bán hàng thông thường' }
                ]}
                value={params.row.ma_mau_bc || ''}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_mau_bc', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'ma_tc_thue',
            headerName: 'Mã tính chất',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ma_tc_thue'
                type='select'
                options={[
                  {
                    value: '1',
                    label:
                      '1. Hàng hoá, dịch vụ dùng riêng cho SXKD chịu thuế GTGT và sử dụng cho các hoạt động cung cấp hàng hoá, dịch vụ không kê khai, nộp thuế GTGT đủ điều kiện khấu trừ thuế'
                  },
                  {
                    value: '2',
                    label: '2. Hàng hoá, dịch vụ dùng chung cho SXKD chịu thuế GTGT và không chịu thuế GTGT'
                  },
                  { value: '3', label: '3. Hàng hóa, dịch vụ dùng cho dự án đầu tư đủ điều kiện khấu trừ thuế' },
                  { value: '4', label: '4. Hàng hóa, dịch vụ không đủ điều kiện khấu trừ' },
                  { value: '5', label: '5. Hàng hóa, dịch vụ không phải tổng hợp trên tờ khai 01/GTGT' }
                ]}
                value={params.row.ma_tc_thue || ''}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_tc_thue', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'ma_kh_thue',
            headerName: 'Mã ncc',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <SearchField<KhachHang>
                type='text'
                searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
                searchColumns={customerSearchColumns}
                columnDisplay='customer_code'
                dialogTitle='Danh mục đối tượng'
                value={params.row.ma_kh_thue_data?.customer_code || ''}
                disabled={formMode === 'view'}
                onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_kh_thue_data', row)}
              />
            ),
            pasteConfig: {
              lookupKey: 'ma_kh_thue',
              resultField: 'ma_kh_thue_data',
              detailEndpoint: QUERY_KEYS.KHACH_HANG
            }
          },
          {
            field: 'ten_kh_thue',
            headerName: 'Tên nhà cung cấp',
            width: 150,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ten_kh_thue'
                type='text'
                value={params.row.ma_kh_thue_data?.customer_name || params.row.ten_kh_thue || ''}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_kh_thue', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'dia_chi',
            headerName: 'Địa chỉ',
            width: 200,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='dia_chi'
                type='text'
                value={params.row.dia_chi || params.row.ma_kh_thue_data?.address || ''}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'dia_chi', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'ma_so_thue',
            headerName: 'Mã số thuế',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ma_so_thue'
                type='text'
                value={params.row.ma_so_thue || params.row.ma_kh_thue_data?.tax_code || ''}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_so_thue', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'ten_vt_thue',
            headerName: 'Tên hàng hóa - dịch vụ',
            width: 150,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ten_vt_thue'
                type='text'
                value={params.row.ten_vt_thue || ''}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_vt_thue', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'thue_nt',
            headerName: 'Thuế NT',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='thue_nt'
                type='number'
                value={params.row.thue_nt || 0.0}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_nt', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'thue',
            headerName: 'Thuế VND',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='thue'
                type='number'
                value={params.row.thue || 0.0}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue', newValue)}
                disabled={formMode === 'view'}
              />
            )
          },
          {
            field: 'ma_kh9',
            headerName: 'Cục thuế',
            width: 120,
            renderCell: (params: InputTableCellParams) => (
              <SearchField<KhachHang>
                type='text'
                searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
                searchColumns={customerSearchColumns}
                columnDisplay='customer_code'
                dialogTitle='Danh mục đối tượng'
                value={params.row.ma_kh9_data?.customer_code || ''}
                disabled={formMode === 'view'}
                onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_kh9_data', row)}
              />
            ),
            pasteConfig: {
              lookupKey: 'ma_kh9',
              resultField: 'ma_kh9_data',
              detailEndpoint: QUERY_KEYS.KHACH_HANG
            }
          },
          {
            field: 'ten_kh9',
            headerName: 'Tên cục thuế',
            width: 150,
            renderCell: (params: InputTableCellParams) => (
              <CellField
                name='ten_kh9'
                type='text'
                value={params.row.ma_kh9_data?.customer_name || ''}
                disabled={formMode === 'view'}
              />
            )
          }
        ]
      : [])
  ];

  const middle4Columns = [
    {
      field: 'tk_no',
      headerName: 'Tài khoản nợ',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <SearchField<AccountModel>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.id_hd_data?.tk_data?.code || params.row.tk_no_data?.code}
          disabled={formMode === 'view'}
          onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_no_data', row)}
        />
      )
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 120,
      renderCell: (params: InputTableCellParams) => (
        <CellField
          name='tien_nt'
          type='number'
          value={params.row.tien_nt || 0.0}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
          disabled={formMode === 'view'}
        />
      )
    }
  ];

  const [_du_cn, ...hoaDonColumns] = middle3Columns;

  const middle5Columns = [
    {
      field: 'tknh2',
      headerName: 'Ngân hàng',
      width: 180,
      renderCell: (params: InputTableCellParams) => (
        <SearchField<TaiKhoanNganHang>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/`}
          searchColumns={taiKhoanNganHangSearchColumns}
          columnDisplay='account_code'
          dialogTitle='Danh mục tài khoản ngân hàng'
          value={params.row.tk_ngan_hang?.account_code || ''}
          disabled={formMode === 'view'}
          onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_ngan_hang', row)}
        />
      ),
      pasteConfig: {
        lookupKey: 'tknh2',
        resultField: 'tk_ngan_hang',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN_NGAN_HANG
      }
    },
    ...hoaDonColumns
  ];

  if (status === '1') {
    return [
      ...headColumns(onCellValueChange, dien_giai, formMode),
      ...middle1Columns,
      ...tailColumns(onCellValueChange, formMode)
    ];
  } else if (status === '2') {
    return [
      ...headColumns(onCellValueChange, dien_giai, formMode),
      ...middle2Columns,
      ...tailColumns(onCellValueChange, formMode)
    ];
  } else if (status === '3') {
    return [
      ...headColumns(onCellValueChange, dien_giai, formMode),
      ...middle3Columns,
      ...tailColumns(onCellValueChange, formMode)
    ];
  } else if (status === '4') {
    return [
      ...headColumns(onCellValueChange, dien_giai, formMode),
      ...middle4Columns,
      ...tailColumns(onCellValueChange, formMode)
    ];
  } else if (status === '5') {
    return [
      ...headColumns(onCellValueChange, dien_giai, formMode),
      ...middle5Columns,
      ...tailColumns(onCellValueChange, formMode)
    ];
  } else {
    return [
      ...headColumns(onCellValueChange, dien_giai, formMode),
      ...middle1Columns,
      ...tailColumns(onCellValueChange, formMode)
    ];
  }
};
